import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Car,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  CornerUpLeft,
  CornerUpRight,
  Crosshair,
  Download,
  Eye,
  Flag,
  Footprints,
  Gauge,
  History,
  Lock,
  MapPin,
  MoreHorizontal,
  Navigation,
  Pencil,
  Play,
  Plus,
  Route,
  Search,
  Settings,
  Signpost,
  SlidersHorizontal,
  Target,
  Trash2,
  Undo2,
  Waypoints,
  type LucideIcon,
} from 'lucide-react';
import {
  ITINERARIES,
  PAGE_SIZE,
  ROUTE_COLOR_OPTIONS,
  buildSteps,
  createItinerary,
  duplicateItinerary,
  formatKm,
  formatLaunches,
  formatMinutes,
  formatSeg,
  formatSpeed,
  launchInsight,
  meanSpeed,
  modeLabel,
  pageCount,
  routeHex,
  routeSummary,
  segmentTitle,
  totalDistance,
  totalDuration,
  touchItinerary,
  type Itinerary,
  type ItineraryChip,
  type ItinerarySort,
  type ItineraryStatus,
  type Launch,
  type Maneuver,
  type PanelSize,
  type PlaybackMarker,
  type Segment,
  type TravelFilter,
  type TravelMode,
} from '../../data/itinerary-mock';
import { ElevationChart, LaunchChart } from './ItineraryCharts';
import ItineraryArt from './ItineraryArt';
import ItineraryMap from './ItineraryMap';
import RouteThumb from './RouteThumb';
import './itinerary.css';

export interface ItineraryViewProps {
  /** True while the itineraries request is in flight. */
  loading?: boolean;
}

type DetailTab = 'segments' | 'route' | 'navigation' | 'launches' | 'settings';
type MenuKind = 'card' | 'segment' | 'header';

interface MenuState {
  kind: MenuKind;
  id: string;
  top: number;
  left: number;
}

type DialogState =
  | { type: 'create' }
  | { type: 'edit' }
  | { type: 'delete'; id: string }
  | { type: 'limit' }
  | { type: 'add-segment' }
  | { type: 'edit-segment'; id: string }
  | { type: 'launch'; id: string }
  | { type: 'full-map' }
  | null;

interface FormState {
  name: string;
  description: string;
  mode: TravelMode;
  speed: string;
  from: string;
  to: string;
  place: string;
  distance: string;
  duration: string;
  segSpeed: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  mode: 'driving',
  speed: '30',
  from: 'Stop',
  to: 'Destination',
  place: '',
  distance: '1.2',
  duration: '4',
  segSpeed: '30',
};

const MANEUVER_ICON: Record<Maneuver, LucideIcon> = {
  start: MapPin,
  left: CornerUpLeft,
  right: CornerUpRight,
  uturn: Undo2,
  'bear-right': CornerUpRight,
  'bear-left': CornerUpLeft,
  straight: Navigation,
  arrive: Flag,
};

const MODE_ICON: Record<TravelMode, LucideIcon> = {
  walking: Footprints,
  driving: Car,
  point: Crosshair,
};

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

function readNumber(value: string, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.round(parsed * 10) / 10;
}

function statusName(status: ItineraryStatus): string {
  if (status === 'locked') return 'Locked';
  if (status === 'draft') return 'Draft';
  return 'Ready';
}

function editedLine(item: Itinerary): string {
  const date = item.dateLabel.startsWith('Edited') ? item.dateLabel : `Edited ${item.dateLabel}`;
  return `${date} • ${formatLaunches(item.launches.length)}`;
}

function splitWhen(label: string): { date: string; time: string } {
  const [date, time] = label.split(', ');
  return { date: date ?? label, time: time ?? '' };
}

function segmentMean(item: Itinerary): number {
  if (item.segments.length === 0) return item.settings.defaultSpeedKmh;
  const total = item.segments.reduce((sum, segment) => sum + segment.avgSpeedKmh, 0);
  return Math.round(total / item.segments.length);
}

function slug(name: string): string {
  const cleaned = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return cleaned || 'itinerary';
}

function downloadJson(filename: string, data: unknown): void {
  try {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch {
    /* Some browsers block the object URL. The toast still confirms the export. */
  }
}

async function copyText(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    }
  } catch {
    /* The toast still confirms the copy. */
  }
}

function Skel({ className = '', ...rest }: { className?: string } & React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={`it-skel ${className}`} {...rest} />;
}

function ItinerarySkeleton() {
  return (
    <div className="it it--skeleton" role="status" aria-busy="true">
      <span className="visually-hidden">Loading itineraries</span>
      <div className="it-board">
        <div className="it-list">
          <div className="it-list__head" data-testid="skeleton-it-header">
            <Skel className="it-skel--btn" />
          </div>
          <div data-testid="skeleton-it-filters">
            <Skel className="it-skel--chips" />
            <Skel className="it-skel--search" />
          </div>
          <div className="it-cards" data-testid="skeleton-it-list">
            <Skel className="it-skel--row" />
            <Skel className="it-skel--row" />
            <Skel className="it-skel--row" />
            <Skel className="it-skel--row" />
          </div>
        </div>
        <div className="it-detail" data-testid="skeleton-it-detail">
          <Skel className="it-skel--line" />
          <div className="it-hero">
            <Skel className="it-skel--map" data-testid="skeleton-it-map" />
            <div>
              <div className="it-stats" data-testid="skeleton-it-stats">
                <Skel className="it-skel--stat" />
                <Skel className="it-skel--stat" />
                <Skel className="it-skel--stat" />
                <Skel className="it-skel--stat" />
                <Skel className="it-skel--stat" />
                <Skel className="it-skel--stat" />
              </div>
              <Skel className="it-skel--chart" data-testid="skeleton-it-chart" />
            </div>
          </div>
          <Skel className="it-skel--tabs" data-testid="skeleton-it-tabs" />
          <div className="it-split">
            <div data-testid="skeleton-it-segments">
              <Skel className="it-skel--row" />
              <Skel className="it-skel--row" />
            </div>
            <Skel className="it-skel--card" data-testid="skeleton-it-segment-detail" />
          </div>
          <div className="it-summarygrid" data-testid="skeleton-it-summary">
            <Skel className="it-skel--stat" />
            <Skel className="it-skel--stat" />
            <Skel className="it-skel--stat" />
          </div>
          <Skel className="it-skel--row" data-testid="skeleton-it-launches" />
          <Skel className="it-skel--row" data-testid="skeleton-it-navigation" />
          <Skel className="it-skel--card" data-testid="skeleton-it-settings" />
          <Skel className="it-skel--card" data-testid="skeleton-it-route" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ item }: { item: Itinerary }) {
  if (item.status === 'locked') {
    return (
      <span className="it-badge it-badge--lock">
        <Lock size={12} aria-hidden="true" />
        Locked • {item.lockReason || 'Free limit'}
      </span>
    );
  }
  if (item.status === 'draft') {
    return <span className="it-badge it-badge--draft">Draft</span>;
  }
  return <span className="it-badge it-badge--ready">Ready</span>;
}

function Dialog({
  title,
  wide = false,
  children,
  onClose,
}: {
  title: string;
  wide?: boolean;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="it-modal" role="presentation" onMouseDown={onClose}>
      <div
        className={cx('it-dialog', wide && 'it-dialog--wide')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="it-dialog__head">
          <h2>{title}</h2>
          <button type="button" className="it-iconbtn" aria-label="Close dialog" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const ItineraryView: React.FC<ItineraryViewProps> = ({ loading = false }) => {
  const searchId = useId();
  const seq = useRef(20);
  const menuRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<Itinerary[]>(() => ITINERARIES.map((item) => ({ ...item, segments: item.segments.map((segment) => ({ ...segment })) })));
  const [selectedId, setSelectedId] = useState('new-one');
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [chip, setChip] = useState<ItineraryChip>('all');
  const [sort, setSort] = useState<ItinerarySort>('board');
  const [travel, setTravel] = useState<TravelFilter>('any');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<DetailTab>('segments');
  const [pane, setPane] = useState<'list' | 'detail'>('list');
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [quickOpen, setQuickOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<string | null>(null);

  const nextId = (prefix: string) => {
    seq.current += 1;
    return `${prefix}-${seq.current}`;
  };

  const counts = useMemo(() => ({
    all: items.length,
    recent: items.filter((item) => item.recent).length,
    drafts: items.filter((item) => item.status === 'draft').length,
    locked: items.filter((item) => item.status === 'locked').length,
  }), [items]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const next = items.filter((item) => {
      if (chip === 'recent' && !item.recent) return false;
      if (chip === 'drafts' && item.status !== 'draft') return false;
      if (chip === 'locked' && item.status !== 'locked') return false;
      if (travel !== 'any' && item.settings.travelMode !== travel) return false;
      if (!needle) return true;
      const hay = [
        item.name,
        item.description,
        item.dateLabel,
        ...item.segments.flatMap((segment) => [segment.fromLabel, segment.toLabel, segment.place]),
        ...item.labels.map((label) => `${label.text} ${label.sub ?? ''}`),
      ].join(' ').toLowerCase();
      return hay.includes(needle);
    });
    next.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'distance') return totalDistance(b) - totalDistance(a) || a.boardRank - b.boardRank;
      if (sort === 'launches') return b.launches.length - a.launches.length || a.boardRank - b.boardRank;
      if (sort === 'edited') return b.editedAt - a.editedAt || a.boardRank - b.boardRank;
      return a.boardRank - b.boardRank;
    });
    return next;
  }, [items, chip, query, sort, travel]);

  const pages = pageCount(filtered.length === 0 ? 0 : filtered.length);
  const safePage = Math.min(page, pages);
  const start = (safePage - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);
  const rangeFrom = filtered.length === 0 ? 0 : start + 1;
  const rangeTo = Math.min(filtered.length, start + PAGE_SIZE);
  const selected = filtered.find((item) => item.id === selectedId) ?? null;
  const activeSegment = selected?.segments.find((segment) => segment.id === segmentId) ?? selected?.segments[0] ?? null;

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (!menu && !filterOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (filterRef.current?.contains(target)) return;
      setMenu(null);
      setFilterOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenu(null);
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu, filterOpen]);

  const updateItem = (id: string, recipe: (item: Itinerary) => Itinerary) => {
    setItems((current) => current.map((item) => (item.id === id ? recipe(item) : item)));
  };

  const selectItinerary = (id: string) => {
    setSelectedId(id);
    setSegmentId(null);
    setActiveStep(null);
    setQuickOpen(false);
    setZoom(1);
    setPane('detail');
    setMenu(null);
  };

  const openMenu = (kind: MenuKind, id: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 220;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const top = Math.min(rect.bottom + 6, window.innerHeight - 24);
    setFilterOpen(false);
    setMenu((current) => (current && current.kind === kind && current.id === id ? null : { kind, id, top, left }));
  };

  const findItem = (id: string) => items.find((item) => item.id === id) ?? null;

  const findSegment = (id: string) => {
    for (let index = 0; index < items.length; index += 1) {
      const segment = items[index].segments.find((entry) => entry.id === id);
      if (segment) return { item: items[index], segment };
    }
    return null;
  };

  const clearFilters = () => {
    setQuery('');
    setChip('all');
    setTravel('any');
    setSort('board');
    setPage(1);
    setFilterOpen(false);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setError('');
    setMenu(null);
    setDialog({ type: 'create' });
  };

  const openEdit = () => {
    if (!selected) return;
    setForm((current) => ({
      ...current,
      name: selected.name,
      mode: selected.settings.travelMode,
      speed: String(selected.settings.defaultSpeedKmh),
    }));
    setError('');
    setMenu(null);
    setDialog({ type: 'edit' });
  };

  const submitCreate = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Enter a name.');
      return;
    }
    const created = createItinerary({
      id: nextId('it'),
      name: form.name,
      description: form.description,
    });
    setItems((current) => [created, ...current]);
    setChip('all');
    setQuery('');
    setTravel('any');
    setPage(1);
    setSelectedId(created.id);
    setPane('detail');
    setTab('segments');
    setDialog(null);
    setToast(`Created ${created.name}.`);
  };

  const submitEdit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    if (!form.name.trim()) {
      setError('Enter a name.');
      return;
    }
    const speed = Math.min(300, Math.max(5, readNumber(form.speed, selected.settings.defaultSpeedKmh)));
    updateItem(selected.id, (item) => touchItinerary({
      ...item,
      name: form.name.trim(),
      settings: { ...item.settings, travelMode: form.mode, defaultSpeedKmh: speed },
    }));
    setDialog(null);
    setToast('Itinerary updated.');
  };

  const duplicate = (item: Itinerary) => {
    const copy = duplicateItinerary(item, nextId('it'));
    setItems((current) => [copy, ...current]);
    setChip('all');
    setPage(1);
    setSelectedId(copy.id);
    setPane('detail');
    setMenu(null);
    setToast(`Duplicated ${item.name}.`);
  };

  const exportItem = (item: Itinerary) => {
    downloadJson(`${slug(item.name)}.json`, item);
    setMenu(null);
    setToast(`Exported ${item.name}.`);
  };

  const removeItem = (id: string) => {
    const target = findItem(id);
    setItems((current) => current.filter((item) => item.id !== id));
    if (selectedId === id) {
      const remain = items.filter((item) => item.id !== id);
      setSelectedId(remain[0]?.id ?? '');
    }
    setDialog(null);
    setMenu(null);
    setToast(target ? `Deleted ${target.name}.` : 'Itinerary deleted.');
  };

  const startMock = () => {
    if (!selected) return;
    if (selected.status === 'locked') {
      setDialog({ type: 'limit' });
      return;
    }
    if (selected.status === 'draft') {
      setToast('Finish this draft before a mock run.');
      return;
    }
    const launch: Launch = {
      id: nextId('launch'),
      atLabel: '25 Oct 2026, 10:47 AM',
      durationMin: totalDuration(selected),
      distanceKm: totalDistance(selected),
      avgSpeedKmh: selected.settings.defaultSpeedKmh,
      device: 'Samsung F42',
      appVersion: 'v1.2.3',
      status: 'completed',
    };
    updateItem(selected.id, (item) => touchItinerary({
      ...item,
      launches: [launch, ...item.launches],
    }));
    setToast(`Mock queued for ${selected.name}.`);
  };

  const changeSegments = (item: Itinerary, segments: Segment[], path = item.path, rebuild = false) => {
    updateItem(item.id, (current) => touchItinerary({
      ...current,
      path,
      segments,
      steps: rebuild ? buildSteps(segments) : current.steps,
    }));
  };

  const saveSegment = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !dialog) return;
    const distanceKm = Math.max(0.1, readNumber(form.distance, 1));
    const durationMin = Math.max(1, readNumber(form.duration, 4));
    const avgSpeedKmh = Math.max(1, readNumber(form.segSpeed, selected.settings.defaultSpeedKmh));
    const fromLabel = form.from.trim() || 'Stop';
    const toLabel = form.to.trim() || 'Destination';
    const place = form.place.trim() || 'New stop';
    if (dialog.type === 'add-segment') {
      const last = selected.path[selected.path.length - 1] ?? { x: 20, y: 40 };
      const nextPoint = {
        x: Math.min(96, Math.max(6, last.x + (last.x > 70 ? -14 : 14))),
        y: Math.min(64, Math.max(8, last.y + (last.y > 40 ? -12 : 12))),
      };
      const path = [...selected.path, nextPoint];
      const segment: Segment = {
        id: nextId('seg'),
        fromLabel,
        toLabel,
        place,
        distanceKm,
        durationMin,
        avgSpeedKmh,
        fromIndex: Math.max(0, selected.path.length - 1),
        toIndex: path.length - 1,
      };
      changeSegments(selected, [...selected.segments, segment], path, true);
      setSegmentId(segment.id);
      setToast('Segment added.');
    }
    if (dialog.type === 'edit-segment') {
      const segments = selected.segments.map((segment) => (
        segment.id === dialog.id
          ? { ...segment, fromLabel, toLabel, place, distanceKm, durationMin, avgSpeedKmh }
          : segment
      ));
      changeSegments(selected, segments);
      setToast('Segment updated.');
    }
    setDialog(null);
  };

  const reverseSegment = (id: string) => {
    if (!selected) return;
    const segments = selected.segments.map((segment) => (
      segment.id === id
        ? { ...segment, fromLabel: segment.toLabel, toLabel: segment.fromLabel }
        : segment
    ));
    changeSegments(selected, segments);
    setMenu(null);
    setToast('Segment reversed.');
  };

  const copySegment = (id: string) => {
    if (!selected) return;
    const index = selected.segments.findIndex((segment) => segment.id === id);
    if (index < 0) return;
    const source = selected.segments[index];
    const copy: Segment = { ...source, id: nextId('seg'), toLabel: `${source.toLabel} copy` };
    const segments = [...selected.segments.slice(0, index + 1), copy, ...selected.segments.slice(index + 1)];
    changeSegments(selected, segments, selected.path, true);
    setSegmentId(copy.id);
    setMenu(null);
    setToast('Segment duplicated.');
  };

  const removeSegment = (id: string) => {
    if (!selected) return;
    if (selected.segments.length <= 1) {
      setMenu(null);
      setToast('A route needs at least one segment.');
      return;
    }
    const segments = selected.segments.filter((segment) => segment.id !== id);
    changeSegments(selected, segments, selected.path, true);
    if (segmentId === id) setSegmentId(segments[0]?.id ?? null);
    setMenu(null);
    setToast('Segment removed.');
  };

  const openEditSegment = (id: string) => {
    const found = findSegment(id);
    if (!found) return;
    setForm((current) => ({
      ...current,
      from: found.segment.fromLabel,
      to: found.segment.toLabel,
      place: found.segment.place,
      distance: String(found.segment.distanceKm),
      duration: String(found.segment.durationMin),
      segSpeed: String(found.segment.avgSpeedKmh),
    }));
    setError('');
    setMenu(null);
    setDialog({ type: 'edit-segment', id });
  };

  const openAddSegment = () => {
    if (!selected) return;
    setForm((current) => ({
      ...current,
      from: 'Stop',
      to: 'Destination',
      place: '',
      distance: '1.2',
      duration: '4',
      segSpeed: String(selected.settings.defaultSpeedKmh),
    }));
    setMenu(null);
    setDialog({ type: 'add-segment' });
  };

  const patchSettings = (patch: Partial<Itinerary['settings']>) => {
    if (!selected) return;
    updateItem(selected.id, (item) => touchItinerary({
      ...item,
      settings: { ...item.settings, ...patch },
    }));
  };

  const removeLaunch = (id: string) => {
    if (!selected) return;
    updateItem(selected.id, (item) => touchItinerary({
      ...item,
      launches: item.launches.filter((launch) => launch.id !== id),
    }));
    setToast('Launch removed.');
  };

  const copyLaunch = (launch: Launch) => {
    const line = `${launch.atLabel} · ${formatMinutes(launch.durationMin)} · ${formatKm(launch.distanceKm)} · ${formatSpeed(launch.avgSpeedKmh, 'kmh')}`;
    copyText(line).catch(() => undefined);
    setToast('Launch summary copied.');
  };

  if (loading) return <ItinerarySkeleton />;

  const panel = selected?.settings.panel ?? 'm';
  const showChart = tab === 'segments' || tab === 'launches';
  const menuItem = menu && menu.kind !== 'segment' ? findItem(menu.id) : null;
  const deleteTarget = dialog?.type === 'delete' ? findItem(dialog.id) : null;
  const launchTarget = dialog?.type === 'launch' && selected
    ? selected.launches.find((launch) => launch.id === dialog.id) ?? null
    : null;

  const emptyCopy = () => {
    if (query.trim() || travel !== 'any') {
      return {
        title: 'No routes match',
        text: 'Try another name, place, or travel mode.',
        action: 'Clear filters',
        run: clearFilters,
      };
    }
    if (chip === 'drafts') {
      return {
        title: 'No drafts yet',
        text: 'Draft routes stay here until they are ready to run.',
        action: 'Show all',
        run: clearFilters,
      };
    }
    if (chip === 'locked') {
      return {
        title: 'Nothing locked',
        text: 'Routes over the free limit show up here.',
        action: 'Show all',
        run: clearFilters,
      };
    }
    if (chip === 'recent') {
      return {
        title: 'No recent routes',
        text: 'Routes you edited recently will gather here.',
        action: 'Show all',
        run: clearFilters,
      };
    }
    return {
      title: 'No itineraries yet',
      text: 'Create a route and it will show up in this list.',
      action: 'New itinerary',
      run: openCreate,
    };
  };

  const blank = emptyCopy();

  return (
    <div className={`it is-${panel}`} data-pane={pane}>
      <div className="it-board">
        <section className="it-list" aria-label="Itinerary list">
          <header className="it-list__head">
            <button type="button" className="it-btn it-btn--primary" onClick={openCreate}>
              <Plus size={16} aria-hidden="true" />
              New itinerary
            </button>
          </header>

          <div className="it-chipbar" role="group" aria-label="Itinerary filters">
            {([
              ['all', 'All', counts.all],
              ['recent', 'Recent', counts.recent],
              ['drafts', 'Drafts', counts.drafts],
              ['locked', 'Locked', counts.locked],
            ] as const).map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                className="it-chip"
                aria-pressed={chip === id}
                onClick={() => {
                  setChip(id);
                  setPage(1);
                }}
              >
                {label}
                <span className="it-count">{count}</span>
              </button>
            ))}
          </div>

          <div className="it-tools">
            <label className="it-search">
              <Search size={16} aria-hidden="true" />
              <span className="visually-hidden" id={searchId}>Search itineraries</span>
              <input
                type="search"
                value={query}
                placeholder="Search itineraries by name or location..."
                aria-labelledby={searchId}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
              />
            </label>
            <div className="it-filter" ref={filterRef}>
              <button
                type="button"
                className={cx('it-iconbtn', filterOpen && 'is-on')}
                aria-label="Filter and sort"
                aria-expanded={filterOpen}
                onClick={() => {
                  setMenu(null);
                  setFilterOpen((open) => !open);
                }}
              >
                <SlidersHorizontal size={16} aria-hidden="true" />
              </button>
              {filterOpen && (
                <div className="it-pop" role="region" aria-label="Filter and sort">
                  <p className="it-pop__label">Sort</p>
                  {([
                    ['board', 'Sort by saved order', 'Saved order'],
                    ['edited', 'Sort by recent edit', 'Recently edited'],
                    ['name', 'Sort by name', 'Name'],
                    ['distance', 'Sort by distance', 'Distance'],
                    ['launches', 'Sort by launches', 'Launches'],
                  ] as const).map(([id, label, text]) => (
                    <button
                      key={id}
                      type="button"
                      className="it-choice"
                      aria-pressed={sort === id}
                      aria-label={label}
                      onClick={() => {
                        setSort(id);
                        setPage(1);
                      }}
                    >
                      {text}
                    </button>
                  ))}
                  <p className="it-pop__label">Travel mode</p>
                  {([
                    ['any', 'Show all travel modes', 'Any'],
                    ['driving', 'Show driving routes', 'Driving'],
                    ['walking', 'Show walking routes', 'Walking'],
                    ['point', 'Show point to point routes', 'Point to point'],
                  ] as const).map(([id, label, text]) => (
                    <button
                      key={id}
                      type="button"
                      className="it-choice"
                      aria-pressed={travel === id}
                      aria-label={label}
                      onClick={() => {
                        setTravel(id);
                        setPage(1);
                      }}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="it-empty" data-testid="itinerary-empty">
              <ItineraryArt />
              <h2>{blank.title}</h2>
              <p>{blank.text}</p>
              <button type="button" className="it-btn it-btn--primary" onClick={blank.run}>
                {blank.action}
              </button>
            </div>
          ) : (
            <div className="it-cards">
              {visible.map((item) => (
                <article
                  key={item.id}
                  className={cx('it-card', selected?.id === item.id && 'is-on')}
                  data-testid="itinerary-card"
                >
                  <button
                    type="button"
                    className="it-card__hit"
                    aria-pressed={selected?.id === item.id}
                    aria-label={`${item.name}, ${statusName(item.status)}`}
                    onClick={() => selectItinerary(item.id)}
                  />
                  <RouteThumb path={item.path} color={routeHex(item.settings.color)} locked={item.status === 'locked'} />
                  <div className="it-card__main">
                    <div className="it-card__top">
                      <p className="it-card__name">{item.name}</p>
                      <StatusBadge item={item} />
                      <button
                        type="button"
                        className="it-card__menu"
                        aria-label={`Actions for ${item.name}`}
                        aria-expanded={menu?.kind === 'card' && menu.id === item.id}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => openMenu('card', item.id, event)}
                      >
                        <MoreHorizontal size={16} aria-hidden="true" />
                      </button>
                    </div>
                    <p className="it-card__date">{item.dateLabel}</p>
                    <div className="it-card__meta">
                      <span className="it-meta it-meta--blue"><Waypoints size={13} aria-hidden="true" />{formatSeg(item.segments.length)}</span>
                      <span className="it-meta it-meta--blue"><MapPin size={13} aria-hidden="true" />{formatKm(totalDistance(item))}</span>
                      <span className="it-meta it-meta--orange"><Clock size={13} aria-hidden="true" />{formatMinutes(totalDuration(item))}</span>
                    </div>
                    <div className="it-card__foot">
                      <span className="it-speed">Avg. speed {formatSpeed(item.settings.defaultSpeedKmh, item.settings.speedUnit)}</span>
                      <span className="it-launches"><BarChart3 size={13} aria-hidden="true" />{formatLaunches(item.launches.length)}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {filtered.length > 0 && (
            <nav className="it-pager" aria-label="Itinerary pages">
              <p>{`Showing ${rangeFrom}-${rangeTo} of ${filtered.length} itineraries`}</p>
              <div className="it-pages">
                <button type="button" className="it-page" aria-label="Previous page" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                  <ChevronLeft size={16} aria-hidden="true" />
                </button>
                {Array.from({ length: pages }, (_, index) => {
                  const number = index + 1;
                  return (
                    <button
                      key={number}
                      type="button"
                      className="it-page"
                      aria-label={`Page ${number}`}
                      aria-current={number === safePage ? 'page' : undefined}
                      onClick={() => setPage(number)}
                    >
                      {number}
                    </button>
                  );
                })}
                <button type="button" className="it-page" aria-label="Next page" disabled={safePage >= pages} onClick={() => setPage(safePage + 1)}>
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </nav>
          )}
        </section>

        <section className="it-detail" aria-label="Itinerary details">
          {selected ? (
            <>
              <button type="button" className="it-back" onClick={() => setPane('list')}>
                <ArrowLeft size={16} aria-hidden="true" />
                Back to itineraries
              </button>
              <header className="it-detail__head">
                <div>
                  <div className="it-name">
                    <h2>{selected.name}</h2>
                    <StatusBadge item={selected} />
                    <button type="button" className="it-iconbtn" aria-label={`Rename ${selected.name}`} onClick={openEdit}>
                      <Pencil size={16} aria-hidden="true" />
                    </button>
                  </div>
                  <p className="it-detail__meta">{editedLine(selected)}</p>
                  {selected.description ? <p className="it-detail__desc">{selected.description}</p> : null}
                </div>
                <div className="it-detail__actions">
                  <button type="button" className="it-btn it-btn--go" onClick={startMock}>
                    <Play size={16} aria-hidden="true" />
                    Start mock
                  </button>
                  <button type="button" className="it-btn it-btn--ghost" onClick={openEdit}>
                    <Pencil size={16} aria-hidden="true" />
                    Edit itinerary
                  </button>
                  <button
                    type="button"
                    className="it-iconbtn"
                    aria-label={`More actions for ${selected.name}`}
                    aria-expanded={menu?.kind === 'header' && menu.id === selected.id}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => openMenu('header', selected.id, event)}
                  >
                    <MoreHorizontal size={16} aria-hidden="true" />
                  </button>
                </div>
              </header>

              <div className="it-hero">
                <div className="it-mapwrap" ref={mapRef}>
                  <ItineraryMap
                    name={selected.name}
                    path={selected.path}
                    labels={selected.labels}
                    color={routeHex(selected.settings.color)}
                    weight={Math.max(1.4, selected.settings.routeWidth / 4)}
                    zoom={zoom}
                    marker={selected.settings.marker}
                    activeFrom={tab === 'segments' && activeSegment ? activeSegment.fromIndex : null}
                    activeTo={tab === 'segments' && activeSegment ? activeSegment.toIndex : null}
                    onZoom={setZoom}
                    onReset={() => setZoom(1)}
                    onExpand={() => setDialog({ type: 'full-map' })}
                  />
                </div>
                <div className="it-side">
                  <div className="it-stats">
                    <article className="it-stat">
                      <span className="it-stat__icon it-stat__icon--blue"><MapPin size={16} aria-hidden="true" /></span>
                      <strong className="it-stat__value">{formatKm(totalDistance(selected))}</strong>
                      <span className="it-stat__label">Distance</span>
                    </article>
                    <article className="it-stat">
                      <span className="it-stat__icon it-stat__icon--orange"><Clock size={16} aria-hidden="true" /></span>
                      <strong className="it-stat__value">{formatMinutes(totalDuration(selected))}</strong>
                      <span className="it-stat__label">Est. duration</span>
                    </article>
                    <article className="it-stat">
                      <span className="it-stat__icon it-stat__icon--orange"><Gauge size={16} aria-hidden="true" /></span>
                      <strong className="it-stat__value">{formatSpeed(selected.settings.defaultSpeedKmh, selected.settings.speedUnit)}</strong>
                      <span className="it-stat__label">Default speed</span>
                    </article>
                    <article className="it-stat">
                      <span className="it-stat__icon it-stat__icon--blue"><Waypoints size={16} aria-hidden="true" /></span>
                      <strong className="it-stat__value">{selected.segments.length}</strong>
                      <span className="it-stat__label">Segments</span>
                    </article>
                    <article className="it-stat">
                      <span className="it-stat__icon it-stat__icon--purple"><Signpost size={16} aria-hidden="true" /></span>
                      <strong className="it-stat__value">{selected.steps.length}</strong>
                      <span className="it-stat__label">Navigation steps</span>
                    </article>
                    <article className="it-stat">
                      <span className="it-stat__icon it-stat__icon--green"><Target size={16} aria-hidden="true" /></span>
                      <strong className="it-stat__value">{selected.settings.accuracyM} m</strong>
                      <span className="it-stat__label">Default accuracy</span>
                    </article>
                  </div>
                  {showChart ? (
                    <LaunchChart
                      title={tab === 'segments' ? 'Launch stats' : 'Launch activity'}
                      bars={selected.launchBars}
                      total={selected.launches.length}
                    />
                  ) : (
                    <ElevationChart points={selected.elevation} />
                  )}
                </div>
              </div>

              <div className="it-sheet">
                <div className="it-tabs" role="tablist" aria-label="Itinerary sections">
                  {([
                    ['segments', `Segments (${selected.segments.length})`, Waypoints],
                    ['route', 'Route details', MapPin],
                    ['navigation', `Navigation steps (${selected.steps.length})`, Navigation],
                    ['launches', `Launch history (${selected.launches.length})`, History],
                    ['settings', 'Settings', Settings],
                  ] as const).map(([id, label, Icon]) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      className="it-tab"
                      id={`it-tab-${id}`}
                      aria-selected={tab === id}
                      aria-controls={`it-panel-${id}`}
                      onClick={() => setTab(id)}
                    >
                      <Icon size={15} aria-hidden="true" />
                      {label}
                    </button>
                  ))}
                </div>

                <div className="it-sheet__body" role="tabpanel" id={`it-panel-${tab}`} aria-labelledby={`it-tab-${tab}`}>
                  {tab === 'segments' && activeSegment && (
                    <>
                      <div className="it-split">
                        <section className="it-block">
                          <div className="it-block__head">
                            <div>
                              <h3>Route segments</h3>
                              <p>List of all segments in this itinerary.</p>
                            </div>
                            <button type="button" className="it-btn it-btn--ghost it-btn--small" onClick={openAddSegment}>
                              <Plus size={14} aria-hidden="true" />
                              Add segment
                            </button>
                          </div>
                          {!selected.settings.showRouteDetails && (
                            <p className="it-note">Route details between points are hidden.</p>
                          )}
                          <div className="it-segs">
                            {selected.segments.map((segment, index) => (
                              <article key={segment.id} className={cx('it-seg', activeSegment.id === segment.id && 'is-on')}>
                                <span className="it-seg__rail" aria-hidden="true">
                                  <span className="it-seg__no">{index + 1}</span>
                                </span>
                                <button type="button" className="it-seg__open" onClick={() => setSegmentId(segment.id)}>
                                  <span className="it-seg__body">
                                    <strong>{segmentTitle(segment)}</strong>
                                    <span className="it-seg__place">{segment.place}</span>
                                    <span className="it-seg__stats">
                                      {selected.settings.showRouteDetails && (
                                        <>
                                          <span><Route size={13} aria-hidden="true" />{formatKm(segment.distanceKm)}</span>
                                          <span><Clock size={13} aria-hidden="true" />{formatMinutes(segment.durationMin)}</span>
                                        </>
                                      )}
                                      <span><Gauge size={13} aria-hidden="true" />Avg. {formatSpeed(segment.avgSpeedKmh, selected.settings.speedUnit)}</span>
                                    </span>
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  className="it-iconbtn"
                                  aria-label={`Segment actions, ${segment.fromLabel} to ${segment.toLabel}`}
                                  aria-expanded={menu?.kind === 'segment' && menu.id === segment.id}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => openMenu('segment', segment.id, event)}
                                >
                                  <MoreHorizontal size={16} aria-hidden="true" />
                                </button>
                              </article>
                            ))}
                          </div>
                        </section>

                        <section className="it-block">
                          <div className="it-block__head">
                            <div>
                              <h3>{`Segment ${selected.segments.findIndex((segment) => segment.id === activeSegment.id) + 1} details`}</h3>
                              <p>{activeSegment.place}</p>
                            </div>
                            <button type="button" className="it-btn it-btn--ghost it-btn--small" onClick={() => openEditSegment(activeSegment.id)}>
                              <Pencil size={14} aria-hidden="true" />
                              Edit
                            </button>
                          </div>
                          <ItineraryMap
                            name={segmentTitle(activeSegment)}
                            path={selected.path}
                            labels={[]}
                            color={routeHex(selected.settings.color)}
                            weight={Math.max(1.6, selected.settings.routeWidth / 4)}
                            zoom={1}
                            marker={selected.settings.marker}
                            activeFrom={activeSegment.fromIndex}
                            activeTo={activeSegment.toIndex}
                            compact
                            testId="itinerary-mini-map"
                          />
                          <div className="it-stats it-stats--3">
                            <article className="it-stat">
                              <strong className="it-stat__value">{formatKm(activeSegment.distanceKm)}</strong>
                              <span className="it-stat__label">Distance</span>
                            </article>
                            <article className="it-stat">
                              <strong className="it-stat__value">{formatMinutes(activeSegment.durationMin)}</strong>
                              <span className="it-stat__label">Duration</span>
                            </article>
                            <article className="it-stat">
                              <strong className="it-stat__value">{formatSpeed(activeSegment.avgSpeedKmh, selected.settings.speedUnit)}</strong>
                              <span className="it-stat__label">Avg. speed</span>
                            </article>
                          </div>
                          <button type="button" className="it-quick" aria-expanded={quickOpen} onClick={() => setQuickOpen((open) => !open)}>
                            Quick actions
                            <ChevronRight size={16} aria-hidden="true" />
                          </button>
                          {quickOpen && (
                            <div className="it-quick__list">
                              <button type="button" onClick={() => openEditSegment(activeSegment.id)}>Edit segment</button>
                              <button type="button" onClick={() => mapRef.current?.scrollIntoView({ block: 'nearest' })}>View on map</button>
                              <button type="button" onClick={() => reverseSegment(activeSegment.id)}>Reverse segment</button>
                            </div>
                          )}
                        </section>
                      </div>

                      <div className="it-summarygrid">
                        <section className="it-block">
                          <h3>Segment summary</h3>
                          <div className="it-stats it-stats--3">
                            <article className="it-stat">
                              <strong className="it-stat__value">{formatKm(totalDistance(selected))}</strong>
                              <span className="it-stat__label">Total distance</span>
                            </article>
                            <article className="it-stat">
                              <strong className="it-stat__value">{formatMinutes(totalDuration(selected))}</strong>
                              <span className="it-stat__label">Total duration</span>
                            </article>
                            <article className="it-stat">
                              <strong className="it-stat__value">{formatSpeed(segmentMean(selected), selected.settings.speedUnit)}</strong>
                              <span className="it-stat__label">Average speed</span>
                            </article>
                          </div>
                        </section>
                        <section className="it-block">
                          <h3>Recent launch</h3>
                          {selected.launches[0] ? (
                            <>
                              <p className="it-launchline">{splitWhen(selected.launches[0].atLabel).date}</p>
                              <p className="it-muted">{splitWhen(selected.launches[0].atLabel).time}</p>
                              <p className="it-muted">{`${formatMinutes(selected.launches[0].durationMin)} · ${formatKm(selected.launches[0].distanceKm)}`}</p>
                              <button type="button" className="it-textbtn" onClick={() => setTab('launches')}>View details</button>
                            </>
                          ) : (
                            <p className="it-muted">No launches yet.</p>
                          )}
                        </section>
                        <section className="it-block">
                          <div className="it-block__head">
                            <h3>Route colour</h3>
                            <button type="button" className="it-iconbtn" aria-label="Edit route colour" onClick={() => setTab('settings')}>
                              <Pencil size={14} aria-hidden="true" />
                            </button>
                          </div>
                          <div className="it-swatches" role="group" aria-label="Route colour">
                            {ROUTE_COLOR_OPTIONS.map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                className="it-swatch"
                                style={{ background: option.hex }}
                                aria-label={`${option.label} route colour`}
                                aria-pressed={selected.settings.color === option.id}
                                onClick={() => patchSettings({ color: option.id })}
                              />
                            ))}
                          </div>
                        </section>
                        <section className="it-block">
                          <h3>More actions</h3>
                          <div className="it-actionrow">
                            <button type="button" className="it-btn it-btn--ghost it-btn--small" onClick={() => duplicate(selected)}>
                              <Copy size={14} aria-hidden="true" />
                              Duplicate
                            </button>
                            <button type="button" className="it-btn it-btn--ghost it-btn--small" onClick={() => exportItem(selected)}>
                              <Download size={14} aria-hidden="true" />
                              Export
                            </button>
                            <button type="button" className="it-btn it-btn--danger it-btn--small" onClick={() => setDialog({ type: 'delete', id: selected.id })}>
                              <Trash2 size={14} aria-hidden="true" />
                              Delete
                            </button>
                          </div>
                        </section>
                      </div>
                    </>
                  )}

                  {tab === 'route' && (
                    <div className="it-stack">
                      <section className="it-block">
                        <div className="it-block__head">
                          <div>
                            <h3>Route overview</h3>
                            <p>Complete information about this itinerary and its route.</p>
                          </div>
                          <button type="button" className="it-btn it-btn--ghost it-btn--small" onClick={() => setDialog({ type: 'full-map' })}>
                            View on full map
                          </button>
                        </div>
                        <div className="it-overview">
                          <article className="it-stat"><span className="it-stat__label">Total distance</span><strong>{formatKm(totalDistance(selected))}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Total duration</span><strong>{formatMinutes(totalDuration(selected))}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Average speed</span><strong>{formatSpeed(selected.settings.defaultSpeedKmh, selected.settings.speedUnit)}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Route type</span><strong>{modeLabel(selected.settings.travelMode)}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Start location</span><strong>{selected.segments[0]?.place}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">End location</span><strong>{selected.segments[selected.segments.length - 1]?.place}</strong></article>
                        </div>
                      </section>
                      <section className="it-block">
                        <h3>Route map & segments</h3>
                        <p className="it-muted">Visual overview of the complete route with all segments.</p>
                        <div className="it-timeline">
                          <span className="it-node">1</span>
                          {selected.segments.map((segment, index) => (
                            <div key={segment.id} className="it-timeline__seg" style={{ flexGrow: Math.max(segment.distanceKm, 0.4) }}>
                              <strong>{`Segment ${index + 1}`}</strong>
                              <small>{`${formatKm(segment.distanceKm)} · ${formatMinutes(segment.durationMin)}`}</small>
                            </div>
                          ))}
                          <span className="it-node">{selected.segments.length > 1 ? selected.segments.length : 2}</span>
                        </div>
                      </section>
                      <section className="it-block">
                        <h3>Segments breakdown</h3>
                        <p className="it-muted">Detailed information for each segment in this itinerary.</p>
                        <div className="it-tablewrap">
                          <table className="it-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>From → To</th>
                                <th>Distance</th>
                                <th>Duration</th>
                                <th>Avg. speed</th>
                                <th>Route type</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selected.segments.map((segment, index) => (
                                <tr key={segment.id}>
                                  <td><span className="it-seg__no">{index + 1}</span></td>
                                  <td>
                                    <strong>{segment.place}</strong>
                                    <span className="it-muted">{segmentTitle(segment)}</span>
                                  </td>
                                  <td>{formatKm(segment.distanceKm)}</td>
                                  <td>{formatMinutes(segment.durationMin)}</td>
                                  <td>{formatSpeed(segment.avgSpeedKmh, selected.settings.speedUnit)}</td>
                                  <td>{modeLabel(selected.settings.travelMode)}</td>
                                  <td className="it-rowactions">
                                    <button type="button" className="it-iconbtn" aria-label={`View ${segmentTitle(segment)}`} onClick={() => { setSegmentId(segment.id); setTab('segments'); }}>
                                      <Eye size={15} aria-hidden="true" />
                                    </button>
                                    <button type="button" className="it-iconbtn" aria-label={`Edit ${segmentTitle(segment)}`} onClick={() => openEditSegment(segment.id)}>
                                      <Pencil size={15} aria-hidden="true" />
                                    </button>
                                    <button type="button" className="it-iconbtn it-iconbtn--danger" aria-label={`Delete ${segmentTitle(segment)}`} onClick={() => removeSegment(segment.id)}>
                                      <Trash2 size={15} aria-hidden="true" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </section>
                      <div className="it-insight">
                        <Route size={18} aria-hidden="true" />
                        <p><strong>Route summary. </strong>{routeSummary(selected)}</p>
                      </div>
                    </div>
                  )}

                  {tab === 'navigation' && (
                    <section className="it-block">
                      <div className="it-block__head">
                        <div>
                          <h3>Navigation steps</h3>
                          <p>Turn-by-turn directions for the complete route (all segments).</p>
                        </div>
                        <button type="button" className="it-btn it-btn--ghost it-btn--small" onClick={() => setDialog({ type: 'full-map' })}>
                          <MapPin size={14} aria-hidden="true" />
                          View on full map
                        </button>
                      </div>
                      {selected.steps.length === 0 ? (
                        <div className="it-empty is-compact">
                          <ItineraryArt />
                          <h3>No navigation steps</h3>
                          <p>Steps appear when the route has segments.</p>
                        </div>
                      ) : (
                        <ol className="it-steps">
                          {selected.steps.map((step, index) => {
                            const Icon = MANEUVER_ICON[step.maneuver];
                            return (
                              <li key={step.id} className={cx('it-step', activeStep === step.id && 'is-on')}>
                                <span className="it-step__no">{index + 1}</span>
                                <span className="it-step__icon"><Icon size={16} aria-hidden="true" /></span>
                                <span className="it-step__text">{step.text}</span>
                                <span className="it-step__dist">
                                  {selected.settings.showRouteDetails ? step.distanceLabel : 'Hidden'}
                                </span>
                                <button
                                  type="button"
                                  className="it-iconbtn"
                                  aria-label={`Focus step ${index + 1}`}
                                  onClick={() => setActiveStep(step.id)}
                                >
                                  <MapPin size={15} aria-hidden="true" />
                                </button>
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </section>
                  )}

                  {tab === 'launches' && (
                    <div className="it-stack">
                      <section className="it-block">
                        <div className="it-block__head">
                          <div>
                            <h3>Launch history</h3>
                            <p>All times this itinerary was launched and run on your device.</p>
                          </div>
                          <span className="it-pill">Last 30 days</span>
                        </div>
                        <div className="it-overview it-overview--5">
                          <article className="it-stat"><span className="it-stat__label">Total launches</span><strong>{selected.launches.length}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Total duration</span><strong>{formatMinutes(selected.launches.reduce((sum, launch) => sum + launch.durationMin, 0))}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Total distance</span><strong>{formatKm(selected.launches.reduce((sum, launch) => sum + launch.distanceKm, 0))}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Avg. speed</span><strong>{formatSpeed(meanSpeed(selected.launches), 'kmh')}</strong></article>
                          <article className="it-stat"><span className="it-stat__label">Last launched</span><strong>{selected.launches[0] ? splitWhen(selected.launches[0].atLabel).date : '—'}</strong></article>
                        </div>
                      </section>
                      {selected.launches.length === 0 ? (
                        <div className="it-empty is-compact" data-testid="itinerary-launches-empty">
                          <ItineraryArt />
                          <h3>No launches yet</h3>
                          <p>Launch history appears after this route runs on a device.</p>
                        </div>
                      ) : (
                        <section className="it-block">
                          <div className="it-tablewrap">
                            <table className="it-table">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Date & time</th>
                                  <th>Duration</th>
                                  <th>Distance</th>
                                  <th>Avg. speed</th>
                                  <th>Device/App</th>
                                  <th>Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selected.launches.map((launch, index) => (
                                  <tr key={launch.id}>
                                    <td><span className="it-seg__no">{index + 1}</span></td>
                                    <td>{launch.atLabel}</td>
                                    <td>{formatMinutes(launch.durationMin)}</td>
                                    <td>{formatKm(launch.distanceKm)}</td>
                                    <td>{formatSpeed(launch.avgSpeedKmh, 'kmh')}</td>
                                    <td>
                                      <strong>{launch.device}</strong>
                                      <span className="it-muted">{launch.appVersion}</span>
                                    </td>
                                    <td><span className="it-dot">Completed</span></td>
                                    <td className="it-rowactions">
                                      <button type="button" className="it-iconbtn" aria-label={`Copy launch ${index + 1}`} onClick={() => copyLaunch(launch)}>
                                        <Copy size={15} aria-hidden="true" />
                                      </button>
                                      <button type="button" className="it-iconbtn" aria-label={`View launch ${index + 1}`} onClick={() => setDialog({ type: 'launch', id: launch.id })}>
                                        <BarChart3 size={15} aria-hidden="true" />
                                      </button>
                                      <button type="button" className="it-iconbtn it-iconbtn--danger" aria-label={`Delete launch ${index + 1}`} onClick={() => removeLaunch(launch.id)}>
                                        <Trash2 size={15} aria-hidden="true" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </section>
                      )}
                      <div className="it-insight">
                        <BarChart3 size={18} aria-hidden="true" />
                        <p><strong>Launch insights. </strong>{launchInsight(selected)}</p>
                      </div>
                    </div>
                  )}

                  {tab === 'settings' && (
                    <div className="it-settings">
                      <section className="it-block it-calc">
                        <h3>Route calculations</h3>
                        <div className="it-switchrow">
                          <p>Show route details. Calculate and show route details between points.</p>
                          <button
                            type="button"
                            role="switch"
                            className="it-switch"
                            aria-label="Show route details"
                            aria-checked={selected.settings.showRouteDetails}
                            onClick={() => patchSettings({ showRouteDetails: !selected.settings.showRouteDetails })}
                          >
                            <span />
                          </button>
                        </div>
                      </section>

                      <section className="it-block it-mode">
                        <h3>Travel mode <span>(default for new segments)</span></h3>
                        <div className="it-modes" role="group" aria-label="Travel mode">
                          {(['walking', 'driving', 'point'] as const).map((mode) => {
                            const Icon = MODE_ICON[mode];
                            return (
                              <button
                                key={mode}
                                type="button"
                                className="it-modebtn"
                                aria-pressed={selected.settings.travelMode === mode}
                                onClick={() => patchSettings({ travelMode: mode })}
                              >
                                <Icon size={16} aria-hidden="true" />
                                {modeLabel(mode)}
                              </button>
                            );
                          })}
                        </div>
                      </section>

                      <section className="it-block it-move">
                        <h3>Movement settings</h3>
                        <label className="it-slider">
                          <span>Default speed <strong>{formatSpeed(selected.settings.defaultSpeedKmh, selected.settings.speedUnit)}</strong></span>
                          <input
                            type="range"
                            min={5}
                            max={300}
                            step={1}
                            value={selected.settings.defaultSpeedKmh}
                            aria-label="Default speed"
                            onChange={(event) => patchSettings({ defaultSpeedKmh: Number(event.target.value) })}
                          />
                        </label>
                        <div className="it-unit" role="group" aria-label="Speed unit">
                          <span>Speed unit</span>
                          <button type="button" aria-pressed={selected.settings.speedUnit === 'kmh'} onClick={() => patchSettings({ speedUnit: 'kmh' })}>km/h</button>
                          <button type="button" aria-pressed={selected.settings.speedUnit === 'mph'} onClick={() => patchSettings({ speedUnit: 'mph' })}>mph</button>
                        </div>
                        <label className="it-slider">
                          <span>Default GPS accuracy <strong>{selected.settings.accuracyM} m</strong></span>
                          <input
                            type="range"
                            min={5}
                            max={100}
                            step={1}
                            value={selected.settings.accuracyM}
                            aria-label="Default GPS accuracy"
                            onChange={(event) => patchSettings({ accuracyM: Number(event.target.value) })}
                          />
                        </label>
                        <label className="it-slider">
                          <span>Route width (on map) <strong>{selected.settings.routeWidth}</strong></span>
                          <input
                            type="range"
                            min={2}
                            max={24}
                            step={1}
                            value={selected.settings.routeWidth}
                            aria-label="Route width"
                            onChange={(event) => patchSettings({ routeWidth: Number(event.target.value) })}
                          />
                        </label>
                      </section>

                      <section className="it-block it-marker">
                        <h3>Playback marker <span>(shown while mock runs)</span></h3>
                        <div className="it-markers" role="group" aria-label="Playback marker">
                          {([
                            ['maps', 'Maps', MapPin],
                            ['car', 'Car', Car],
                            ['waze', 'Waze', Navigation],
                          ] as const).map(([id, label, Icon]) => (
                            <button
                              key={id}
                              type="button"
                              className="it-modebtn"
                              aria-pressed={selected.settings.marker === id}
                              onClick={() => patchSettings({ marker: id as PlaybackMarker })}
                            >
                              <Icon size={16} aria-hidden="true" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </section>

                      <section className="it-block it-colors">
                        <h3>Route appearance</h3>
                        <p className="it-muted">Default route colour</p>
                        <div className="it-swatches" role="group" aria-label="Default route colour">
                          {ROUTE_COLOR_OPTIONS.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              className="it-swatch"
                              style={{ background: option.hex }}
                              aria-label={`${option.label} route colour`}
                              aria-pressed={selected.settings.color === option.id}
                              onClick={() => patchSettings({ color: option.id })}
                            />
                          ))}
                        </div>
                      </section>

                      <section className="it-block it-panel">
                        <h3>Other options</h3>
                        <p className="it-muted">Panel height (destinations list)</p>
                        <div className="it-sizes" role="group" aria-label="Panel height">
                          {([
                            ['s', 'S', 'Small'],
                            ['m', 'M', 'Medium'],
                            ['l', 'L', 'Large'],
                          ] as const).map(([id, letter, label]) => (
                            <button
                              key={id}
                              type="button"
                              aria-pressed={selected.settings.panel === id}
                              aria-label={`${label} panel`}
                              onClick={() => patchSettings({ panel: id as PanelSize })}
                            >
                              <strong>{letter}</strong>
                              <span>{label}</span>
                            </button>
                          ))}
                        </div>
                      </section>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={cx('it-empty', filtered.length === 0 && 'is-compact')} data-testid="itinerary-detail-empty">
              {filtered.length > 0 && <ItineraryArt />}
              <h2>No itinerary selected</h2>
              <p>Choose a route from the list, or clear the filters.</p>
            </div>
          )}
        </section>
      </div>

      {menu?.kind === 'segment' && (
        <div ref={menuRef} className="it-menu" style={{ top: menu.top, left: menu.left }} role="menu">
          <button type="button" role="menuitem" onClick={() => openEditSegment(menu.id)}>Edit segment</button>
          <button type="button" role="menuitem" onClick={() => { setSegmentId(menu.id); setTab('segments'); mapRef.current?.scrollIntoView({ block: 'nearest' }); setMenu(null); }}>View on map</button>
          <button type="button" role="menuitem" onClick={() => reverseSegment(menu.id)}>Reverse segment</button>
          <button type="button" role="menuitem" onClick={() => copySegment(menu.id)}>Duplicate segment</button>
          <button type="button" role="menuitem" className="it-menu__danger" onClick={() => removeSegment(menu.id)}>Delete segment</button>
        </div>
      )}

      {menu && menu.kind !== 'segment' && menuItem && (
        <div ref={menuRef} className="it-menu" style={{ top: menu.top, left: menu.left }} role="menu">
          {menu.kind === 'card' && (
            <button type="button" role="menuitem" onClick={() => selectItinerary(menuItem.id)}>Open</button>
          )}
          <button type="button" role="menuitem" onClick={() => duplicate(menuItem)}>Duplicate</button>
          <button type="button" role="menuitem" onClick={() => exportItem(menuItem)}>Export</button>
          <button type="button" role="menuitem" className="it-menu__danger" onClick={() => { setMenu(null); setDialog({ type: 'delete', id: menuItem.id }); }}>Delete</button>
        </div>
      )}

      {dialog?.type === 'create' && (
        <Dialog title="New itinerary" onClose={() => setDialog(null)}>
          <form onSubmit={submitCreate}>
            <label className="it-field">
              <span>Name</span>
              <input aria-label="Itinerary name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
            </label>
            <label className="it-field">
              <span>Description</span>
              <textarea aria-label="Description" rows={4} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </label>
            {error && <p className="it-error" role="alert">{error}</p>}
            <div className="it-dialog__actions">
              <button type="button" className="it-btn it-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" className="it-btn it-btn--primary">Create itinerary</button>
            </div>
          </form>
        </Dialog>
      )}

      {dialog?.type === 'edit' && (
        <Dialog title="Edit itinerary" onClose={() => setDialog(null)}>
          <form onSubmit={submitEdit}>
            <label className="it-field">
              <span>Name</span>
              <input aria-label="Itinerary name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
            </label>
            <label className="it-field">
              <span>Travel mode</span>
              <select aria-label="Edit travel mode" value={form.mode} onChange={(event) => setForm({ ...form, mode: event.target.value as TravelMode })}>
                <option value="driving">Driving</option>
                <option value="walking">Walking</option>
                <option value="point">Point to point</option>
              </select>
            </label>
            <label className="it-field">
              <span>Default speed (km/h)</span>
              <input aria-label="Edit default speed" inputMode="decimal" value={form.speed} onChange={(event) => setForm({ ...form, speed: event.target.value })} />
            </label>
            {error && <p className="it-error" role="alert">{error}</p>}
            <div className="it-dialog__actions">
              <button type="button" className="it-btn it-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" className="it-btn it-btn--primary">Save changes</button>
            </div>
          </form>
        </Dialog>
      )}

      {(dialog?.type === 'add-segment' || dialog?.type === 'edit-segment') && (
        <Dialog title={dialog.type === 'add-segment' ? 'Add segment' : 'Edit segment'} onClose={() => setDialog(null)}>
          <form onSubmit={saveSegment}>
            <label className="it-field">
              <span>From</span>
              <input aria-label="Segment from" value={form.from} onChange={(event) => setForm({ ...form, from: event.target.value })} />
            </label>
            <label className="it-field">
              <span>To</span>
              <input aria-label="Segment to" value={form.to} onChange={(event) => setForm({ ...form, to: event.target.value })} />
            </label>
            <label className="it-field">
              <span>Place</span>
              <input aria-label="Segment place" value={form.place} onChange={(event) => setForm({ ...form, place: event.target.value })} />
            </label>
            <div className="it-fieldpair">
              <label className="it-field">
                <span>Distance (km)</span>
                <input aria-label="Segment distance" inputMode="decimal" value={form.distance} onChange={(event) => setForm({ ...form, distance: event.target.value })} />
              </label>
              <label className="it-field">
                <span>Duration (min)</span>
                <input aria-label="Segment duration" inputMode="decimal" value={form.duration} onChange={(event) => setForm({ ...form, duration: event.target.value })} />
              </label>
            </div>
            <label className="it-field">
              <span>Average speed (km/h)</span>
              <input aria-label="Segment speed" inputMode="decimal" value={form.segSpeed} onChange={(event) => setForm({ ...form, segSpeed: event.target.value })} />
            </label>
            <div className="it-dialog__actions">
              <button type="button" className="it-btn it-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
              <button type="submit" className="it-btn it-btn--primary">{dialog.type === 'add-segment' ? 'Add this segment' : 'Save segment'}</button>
            </div>
          </form>
        </Dialog>
      )}

      {dialog?.type === 'delete' && deleteTarget && (
        <Dialog title="Delete itinerary" onClose={() => setDialog(null)}>
          <p className="it-dialog__lead">Delete {deleteTarget.name}? This only removes it from the preview list.</p>
          <div className="it-dialog__actions">
            <button type="button" className="it-btn it-btn--ghost" onClick={() => setDialog(null)}>Cancel</button>
            <button type="button" className="it-btn it-btn--danger" onClick={() => removeItem(deleteTarget.id)}>Delete itinerary</button>
          </div>
        </Dialog>
      )}

      {dialog?.type === 'limit' && (
        <Dialog title="Free limit" onClose={() => setDialog(null)}>
          <div className="it-limit" data-testid="itinerary-limit">
            <ItineraryArt />
            <p>This route is over the free limit. Upgrade to run it on a device.</p>
            <div className="it-dialog__actions">
              <button type="button" className="it-btn it-btn--ghost" onClick={() => setDialog(null)}>Close</button>
              <Link className="it-btn it-btn--primary" to="/app/account">View plans</Link>
            </div>
          </div>
        </Dialog>
      )}

      {dialog?.type === 'launch' && launchTarget && (
        <Dialog title="Launch details" onClose={() => setDialog(null)}>
          <dl className="it-defs">
            <div><dt>When</dt><dd>{launchTarget.atLabel}</dd></div>
            <div><dt>Duration</dt><dd>{formatMinutes(launchTarget.durationMin)}</dd></div>
            <div><dt>Distance</dt><dd>{formatKm(launchTarget.distanceKm)}</dd></div>
            <div><dt>Average speed</dt><dd>{formatSpeed(launchTarget.avgSpeedKmh, 'kmh')}</dd></div>
            <div><dt>Device</dt><dd>{`${launchTarget.device} ${launchTarget.appVersion}`}</dd></div>
            <div><dt>Status</dt><dd>Completed</dd></div>
          </dl>
          <div className="it-dialog__actions">
            <button type="button" className="it-btn it-btn--primary" onClick={() => setDialog(null)}>Done</button>
          </div>
        </Dialog>
      )}

      {dialog?.type === 'full-map' && selected && (
        <Dialog title={`${selected.name} map`} wide onClose={() => setDialog(null)}>
          <ItineraryMap
            name={selected.name}
            path={selected.path}
            labels={selected.labels}
            color={routeHex(selected.settings.color)}
            weight={Math.max(1.4, selected.settings.routeWidth / 4)}
            zoom={zoom}
            marker={selected.settings.marker}
            activeFrom={activeSegment ? activeSegment.fromIndex : null}
            activeTo={activeSegment ? activeSegment.toIndex : null}
            testId="itinerary-full-map"
            onZoom={setZoom}
            onReset={() => setZoom(1)}
          />
        </Dialog>
      )}

      {toast && (
        <div className="it-toastwrap">
          <p className="it-toast" role="status">{toast}</p>
        </div>
      )}
    </div>
  );
};

export default ItineraryView;
